// The aligner keeps its own document keymap because the shell's (src/app/keys.ts) reads a project's
// sections and its live editor, which the aligner has none of: this one routes by the edit box.
//
// A capture listener that turns a key into a chord and sends it to the registry through the
// shell's routeKey, unless the key belongs to the palette, the edit box, a field or a dialog.

import { useEffect } from 'react'
import { routeKey } from '../../core/keymap.ts'
import type { Shell } from './shell.ts'

export function useDocumentKeys(shell: Shell): void {
  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      const target = e.target instanceof Element ? e.target : null
      const chord = shell.chordOf(e)
      if (!chord) return
      // The edit box keeps every key, as the shell's editor keeps its own: Cmd+Z undoes typing
      // there and Cmd+Backspace deletes.
      const inEditor = target !== null && target.closest('.editbox') !== null
      // A cell keeps its unmodified keys, the arrows and Enter, as a field does.
      const inField = target !== null && target.matches('input, textarea, select, button, [contenteditable=true]')
      const inMenu = target !== null && target.closest('.menubar, .barmenu') !== null
      const inDialog = target !== null && target.closest('[role=dialog]') !== null && !shell.state.palette
      if (inDialog || routeKey({ paletteOpen: shell.state.palette, inMenu, inEditor, inField }, chord) !== 'document') return
      if (shell.dispatch(chord)) {
        e.preventDefault()
        e.stopPropagation()
      }
    }
    document.addEventListener('keydown', listener, true)
    return () => document.removeEventListener('keydown', listener, true)
  }, [shell])
}
